import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getApiList } from '../api';
import { getUser } from '../auth';

const ACTIVE_LIMIT_MINUTES = 3;
const IDLE_LIMIT_MINUTES = 10;

function Employees() {
  const navigate = useNavigate();
  const currentUser = getUser();

  const [employees, setEmployees] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);
  const [loginLogsLoading, setLoginLogsLoading] = useState(false);
  const [activitySettings, setActivitySettings] = useState({
    active_users_visibility: 'owner_only',
    can_manage: false,
    can_view_activity: false
  });

  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    password: '',
    role: 'manager'
  });

  useEffect(() => {
    loadAll();

    const interval = setInterval(() => {
      loadAll(false);
    }, 45000);

    return () => clearInterval(interval);
  }, []);

  const loadAll = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      setError('');

      const [usersResponse, settingsResponse, loginLogsResponse] = await Promise.all([
        apiFetch('/users'),
        apiFetch('/activity-settings'),
        apiFetch('/login-logs')
      ]);

      if (usersResponse) {
        const usersData = await usersResponse.json();
        if (!usersResponse.ok) {
          setError(usersData.message || usersData.error || 'فشل جلب الموظفين');
        } else {
          setEmployees(getApiList(usersData, 'users'));
        }
      }

      if (settingsResponse) {
        const settingsData = await settingsResponse.json();
        if (settingsResponse.ok) {
          setActivitySettings({
            active_users_visibility: settingsData.active_users_visibility || 'owner_only',
            can_manage: settingsData.can_manage === true,
            can_view_activity: settingsData.can_view_activity === true
          });
        }
      }

      if (loginLogsResponse) {
        const loginLogsData = await loginLogsResponse.json().catch(() => ({}));
        if (loginLogsResponse.ok) {
          setLoginLogs(Array.isArray(loginLogsData.logs) ? loginLogsData.logs : []);
        }
      }
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء جلب بيانات الموظفين');
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const refreshLoginLogs = async () => {
    try {
      setLoginLogsLoading(true);
      const response = await apiFetch('/login-logs');
      if (!response) return;
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setLoginLogs(Array.isArray(data.logs) ? data.logs : []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoginLogsLoading(false);
    }
  };

  const normalizeArabic = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/[أإآا]/g, 'ا')
      .replace(/[ى]/g, 'ي')
      .replace(/[ؤ]/g, 'و')
      .replace(/[ئ]/g, 'ي')
      .replace(/[ة]/g, 'ه')
      .replace(/[٠-٩]/g, (digit) => '٠١٢٣٤٥٦٧٨٩'.indexOf(digit))
      .replace(/\s+/g, ' ')
      .trim();
  };

  const formatDate = (value) => {
    if (!value) return '-';

    try {
      return new Date(value).toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const formatNumber = (value) => {
    const number = Number(value || 0);
    if (!Number.isFinite(number)) return '0';

    return number.toLocaleString('en-US', {
      maximumFractionDigits: 2
    });
  };

  const getMinutesAgo = (value) => {
    if (!value) return null;
    const time = new Date(value).getTime();
    if (!Number.isFinite(time)) return null;
    return Math.max(0, Math.floor((Date.now() - time) / 60000));
  };

  const getActivityStatus = (employee) => {
    // Prefer the server-computed status because D1 timestamps may be parsed
    // differently by browsers/timezones. Backend already returns:
    // active_status: active | idle | offline, active_status_label, minutes_since_seen.
    const backendStatus = String(employee?.active_status || '').toLowerCase();
    const backendLabel = employee?.active_status_label;
    const backendMinutes = Number(employee?.minutes_since_seen);

    if (backendStatus === 'active' || employee?.is_online === true) {
      return {
        key: 'online',
        label: backendLabel || 'نشط الآن',
        hint: 'نشط الآن داخل النظام',
        dot: '🟢',
        className: 'bg-green-100 text-green-800 border-green-200'
      };
    }

    if (backendStatus === 'idle') {
      return {
        key: 'idle',
        label: backendLabel || (Number.isFinite(backendMinutes) ? `خامل منذ ${backendMinutes} دقائق` : 'خامل'),
        hint: 'كان متصلاً قريباً',
        dot: '🟡',
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200'
      };
    }

    if (backendStatus === 'offline') {
      return {
        key: 'offline',
        label: backendLabel || 'غير نشط',
        hint: 'آخر ظهور محفوظ في السجل',
        dot: '⚫',
        className: 'bg-gray-100 text-gray-700 border-gray-200'
      };
    }

    const minutes = Number.isFinite(backendMinutes) ? backendMinutes : getMinutesAgo(employee.last_seen_at);

    if (minutes === null) {
      return {
        key: 'unknown',
        label: 'غير معروف',
        hint: 'لا يوجد نشاط مسجل بعد',
        dot: '⚪',
        className: 'bg-gray-100 text-gray-700 border-gray-200'
      };
    }

    if (minutes < ACTIVE_LIMIT_MINUTES) {
      return {
        key: 'online',
        label: 'نشط الآن',
        hint: 'نشط خلال آخر 3 دقائق',
        dot: '🟢',
        className: 'bg-green-100 text-green-800 border-green-200'
      };
    }

    if (minutes < IDLE_LIMIT_MINUTES) {
      return {
        key: 'idle',
        label: `خامل منذ ${minutes} دقائق`,
        hint: 'كان متصلاً قريباً',
        dot: '🟡',
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200'
      };
    }

    return {
      key: 'offline',
      label: 'غير نشط',
      hint: 'آخر ظهور محفوظ في السجل',
      dot: '⚫',
      className: 'bg-gray-100 text-gray-700 border-gray-200'
    };
  };

  const getRelativeTime = (value) => {
    const minutes = getMinutesAgo(value);
    if (minutes === null) return '-';
    if (minutes < 1) return 'الآن';
    if (minutes < 60) return `منذ ${minutes} دقيقة`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `منذ ${hours} ساعة`;

    const days = Math.floor(hours / 24);
    return `منذ ${days} يوم`;
  };

  const getLastSeenText = (employee) => {
    const status = getActivityStatus(employee);
    const backendMinutes = Number(employee?.minutes_since_seen);

    if (status.key === 'online') return 'متصل الآن';
    if (status.key === 'idle') {
      if (Number.isFinite(backendMinutes)) return `منذ ${backendMinutes} دقائق`;
      return getRelativeTime(employee?.last_seen_at);
    }

    if (Number.isFinite(backendMinutes)) {
      if (backendMinutes < 1) return 'الآن';
      if (backendMinutes < 60) return `منذ ${backendMinutes} دقيقة`;
      const hours = Math.floor(backendMinutes / 60);
      if (hours < 24) return `منذ ${hours} ساعة`;
      return `منذ ${Math.floor(hours / 24)} يوم`;
    }

    return getRelativeTime(employee?.last_seen_at);
  };

  const getDeviceLabel = (deviceInfo) => {
    const text = String(deviceInfo || '').toLowerCase();
    if (!text) return '-';
    if (text.includes('android')) return 'هاتف Android';
    if (text.includes('iphone') || text.includes('ipad')) return 'هاتف iPhone / iPad';
    if (text.includes('mobile')) return 'هاتف';
    if (text.includes('windows')) return 'كمبيوتر Windows';
    if (text.includes('mac')) return 'كمبيوتر Mac';
    return 'جهاز غير محدد';
  };

  const getPageLabel = (page) => {
    const value = String(page || '');
    const labels = {
      '/': 'الرئيسية',
      '/dashboard': 'لوحة التحكم',
      '/stores': 'المحلات',
      '/wallets': 'المحافظ',
      '/transactions': 'العمليات',
      '/user-balance': 'رصيدي',
      '/employees': 'الموظفين',
      '/audit-log': 'التدقيق',
      '/reports': 'التقارير',
      '/settings': 'الإعدادات',
      '/notifications': 'الإشعارات',
      login: 'تسجيل الدخول'
    };

    return labels[value] || value || '-';
  };

  const getRoleIcon = (role) => {
    if (role === 'owner') return '👑';
    return '👨‍💼';
  };

  const getRoleLabel = (role) => {
    if (role === 'owner') return 'مالك';
    if (role === 'manager') return 'مدير';
    return role || '-';
  };

  const getRoleClass = (role) => {
    if (role === 'owner') {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }

    return 'bg-purple-100 text-purple-800 border-purple-200';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ name: '', password: '', role: 'manager' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('اسم الموظف مطلوب');
      return;
    }

    if (!formData.password || formData.password.length < 4) {
      setError('كلمة المرور يجب أن تكون 4 أرقام أو أحرف على الأقل');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const response = await apiFetch('/users', {
        method: 'POST',
        body: JSON.stringify({
          name: formData.name.trim(),
          password: formData.password,
          role: 'manager'
        })
      });

      if (!response) return;

      const data = await response.json();
      if (!response.ok) {
        setError(data.message || data.error || 'فشل إنشاء الموظف');
        return;
      }

      setSuccess('تم إنشاء المدير بنجاح');
      resetForm();
      setShowForm(false);
      loadAll(false);
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء إنشاء الموظف');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (employee) => {
    if (employee.role === 'owner') {
      setError('لا يمكن حذف المالك');
      return;
    }

    if (employee.id === currentUser?.id) {
      setError('لا يمكنك حذف حسابك الحالي');
      return;
    }

    const confirmed = window.confirm(`هل أنت متأكد من حذف المستخدم "${employee.name}"؟`);
    if (!confirmed) return;

    try {
      setDeletingId(employee.id);
      setError('');
      setSuccess('');

      const response = await apiFetch(`/users/${employee.id}`, { method: 'DELETE' });
      if (!response) return;

      const data = await response.json();
      if (!response.ok) {
        setError(data.message || data.error || 'فشل حذف الموظف');
        return;
      }

      setSuccess('تم حذف الموظف بنجاح');
      loadAll(false);
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء حذف الموظف');
    } finally {
      setDeletingId('');
    }
  };

  const handleVisibilityChange = async (value) => {
    try {
      setSavingVisibility(true);
      setError('');
      setSuccess('');

      const response = await apiFetch('/activity-settings', {
        method: 'PUT',
        body: JSON.stringify({ active_users_visibility: value })
      });

      if (!response) return;

      const data = await response.json();
      if (!response.ok) {
        setError(data.message || data.error || 'فشل تحديث صلاحية عرض النشاط');
        return;
      }

      setActivitySettings((prev) => ({
        ...prev,
        active_users_visibility: data.active_users_visibility || value
      }));
      setSuccess('تم تحديث من يستطيع رؤية المستخدمين النشطين');
      loadAll(false);
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء تحديث صلاحية عرض النشاط');
    } finally {
      setSavingVisibility(false);
    }
  };

  const filteredEmployees = useMemo(() => {
    const query = normalizeArabic(searchText);

    return employees
      .filter((employee) => {
        if (roleFilter !== 'all' && employee.role !== roleFilter) return false;

        if (statusFilter !== 'all') {
          const status = getActivityStatus(employee).key;
          if (statusFilter === 'online' && status !== 'online') return false;
          if (statusFilter === 'idle' && status !== 'idle') return false;
          if (statusFilter === 'offline' && status !== 'offline') return false;
        }

        return true;
      })
      .map((employee) => {
        const fields = [
          employee.id,
          employee.name,
          employee.role,
          getRoleLabel(employee.role),
          employee.created_at,
          employee.balance,
          getPageLabel(employee.last_page),
          getDeviceLabel(employee.device_info),
          getActivityStatus(employee).label
        ];

        const text = normalizeArabic(fields.filter(Boolean).join(' | '));
        const name = normalizeArabic(employee.name);
        let score = 10;

        if (query) {
          score = 0;
          if (name.startsWith(query)) score = 100;
          else if (text.startsWith(query)) score = 80;
          else if (text.includes(query)) score = 50;
        }

        return { employee, score };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => {
        const aStatus = getActivityStatus(a.employee).key;
        const bStatus = getActivityStatus(b.employee).key;
        const order = { online: 4, idle: 3, offline: 2, unknown: 1 };

        if ((order[bStatus] || 0) !== (order[aStatus] || 0)) {
          return (order[bStatus] || 0) - (order[aStatus] || 0);
        }

        if (b.score !== a.score) return b.score - a.score;
        if (a.employee.role === 'owner' && b.employee.role !== 'owner') return -1;
        if (a.employee.role !== 'owner' && b.employee.role === 'owner') return 1;

        return new Date(b.employee.last_seen_at || b.employee.created_at || 0) - new Date(a.employee.last_seen_at || a.employee.created_at || 0);
      })
      .map((item) => item.employee);
  }, [employees, searchText, roleFilter, statusFilter]);

  const stats = useMemo(() => {
    const owners = employees.filter((employee) => employee.role === 'owner').length;
    const managers = employees.filter((employee) => employee.role === 'manager').length;
    const online = employees.filter((employee) => getActivityStatus(employee).key === 'online').length;
    const idle = employees.filter((employee) => getActivityStatus(employee).key === 'idle').length;

    return { owners, managers, online, idle, total: employees.length };
  }, [employees]);

  const onlineEmployees = useMemo(() => {
    return employees
      .filter((employee) => ['online', 'idle'].includes(getActivityStatus(employee).key))
      .sort((a, b) => new Date(b.last_seen_at || 0) - new Date(a.last_seen_at || 0));
  }, [employees]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50" dir="rtl">
        <div className="bg-white rounded-3xl shadow p-8 text-xl font-bold text-gray-700">
          جاري تحميل الموظفين...
        </div>
      </div>
    );
  }

  const canSeeActivity = activitySettings.can_view_activity || activitySettings.can_manage;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="bg-gradient-to-br from-gray-950 via-orange-900 to-black text-white rounded-3xl p-5 md:p-8 shadow-lg mb-6">
          <button
            onClick={() => navigate('/')}
            className="bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-xl mb-5"
          >
            ← رجوع للرئيسية
          </button>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold">👨‍💼 إدارة الموظفين</h1>
              <p className="text-orange-100 mt-3 text-sm md:text-base">
                إدارة المستخدمين والصلاحيات مع متابعة من يستخدم النظام الآن
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
              <MiniStat label="الإجمالي" value={stats.total} />
              <MiniStat label="متصل" value={stats.online} />
              <MiniStat label="غير نشط" value={stats.idle} />
              <MiniStat label="مدراء" value={stats.managers} />
              <MiniStat label="مالك" value={stats.owners} />
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 mb-6">
            ⚠️ {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 rounded-2xl px-4 py-3 mb-6">
            ✅ {success}
          </div>
        )}

        <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">👁️ من يستطيع رؤية المستخدمين النشطين؟</h2>
              <p className="text-gray-500 text-sm mt-1">
                المالك يحدد هل تظهر حالة النشاط للمالك فقط أو للمدراء أيضاً.
              </p>
            </div>

            {activitySettings.can_manage ? (
              <select
                value={activitySettings.active_users_visibility}
                onChange={(e) => handleVisibilityChange(e.target.value)}
                disabled={savingVisibility}
                className="border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50 font-bold min-w-[260px]"
              >
                <option value="owner_only">المالك فقط</option>
                <option value="all_managers">المالك والمدراء</option>
              </select>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 text-gray-700 font-bold">
                {activitySettings.active_users_visibility === 'all_managers'
                  ? 'مسموح للمدراء برؤية النشاط'
                  : 'رؤية النشاط محصورة على المالك'}
              </div>
            )}
          </div>
        </div>

        {canSeeActivity ? (
          <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">🟢 المستخدمون النشطون الآن</h2>
                <p className="text-gray-500 text-sm mt-1">
                  المتصل = آخر نشاط أقل من {ACTIVE_LIMIT_MINUTES} دقائق، وغير نشط = أقل من {IDLE_LIMIT_MINUTES} دقائق.
                </p>
              </div>

              <button
                onClick={() => loadAll(false)}
                className="bg-gray-800 hover:bg-black text-white font-bold py-3 px-5 rounded-xl"
              >
                🔄 تحديث
              </button>
            </div>

            {onlineEmployees.length === 0 ? (
              <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-2xl">
                لا يوجد مستخدمون نشطون الآن
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {onlineEmployees.map((employee) => (
                  <ActiveUserCard
                    key={employee.id}
                    employee={employee}
                    getActivityStatus={getActivityStatus}
                    getRoleLabel={getRoleLabel}
                    getPageLabel={getPageLabel}
                    getDeviceLabel={getDeviceLabel}
                    getRelativeTime={getRelativeTime}
                    getLastSeenText={getLastSeenText}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-3xl p-5 md:p-6 mb-6 text-amber-800">
            🔒 حالة المستخدمين النشطين لا تظهر إلا للمالك حالياً.
          </div>
        )}

        <LoginHistorySection
          logs={loginLogs}
          loading={loginLogsLoading}
          onRefresh={refreshLoginLogs}
          formatDate={formatDate}
          canSee={canSeeActivity}
        />

        <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">🔐 قواعد المستخدمين</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <RuleCard title="المالك واحد فقط" text="الاشارة التقنية" color="yellow" />
            <RuleCard title="**" text="****" color="purple" />
            <RuleCard title="متابعة النشاط" text="النظام يسجل آخر ظهور وآخر صفحة بدون التأثير على العمليات المالية." color="green" />
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">🔎 البحث الذكي في الموظفين</h2>
              <p className="text-gray-500 text-sm mt-1">
                ابحث باسم الموظف، الصلاحية، الحالة، آخر صفحة أو نوع الجهاز
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  setShowForm(!showForm);
                  setError('');
                  setSuccess('');
                }}
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-5 rounded-xl"
              >
                {showForm ? 'إغلاق النموذج' : '➕ مدير جديد'}
              </button>

              <button
                onClick={() => loadAll(false)}
                className="bg-gray-800 hover:bg-black text-white font-bold py-3 px-5 rounded-xl"
              >
                🔄 تحديث
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="اكتب أول حرف أو اسم أو صلاحية أو حالة..."
              className="lg:col-span-3 w-full border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            />

            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            >
              <option value="all">كل الصلاحيات</option>
              <option value="owner">المالك</option>
              <option value="manager">المدراء</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            >
              <option value="all">كل الحالات</option>
              <option value="online">متصل الآن</option>
              <option value="idle">غير نشط</option>
              <option value="offline">غير متصل</option>
            </select>
          </div>
        </div>

        {showForm && (
          <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-5">➕ إضافة مدير جديد</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 font-bold mb-2">اسم المدير</label>
                <input
                  type="text"
                  name="name"
                  placeholder="مثال: محمد"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full border-2 border-gray-200 rounded-2xl p-3 outline-none focus:border-orange-500 bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-bold mb-2">كلمة المرور</label>
                <input
                  type="password"
                  name="password"
                  placeholder="4 أرقام أو أحرف فأكثر"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="w-full border-2 border-gray-200 rounded-2xl p-3 outline-none focus:border-orange-500 bg-gray-50"
                />
              </div>

              <div className="md:col-span-2 bg-purple-50 border border-purple-100 rounded-2xl p-4">
                <p className="text-purple-800 font-bold">👨‍💼 الصلاحية: مدير</p>
                <p className="text-gray-600 text-sm mt-1">
                  اعانكم الله
                </p>
              </div>

              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white font-bold py-4 rounded-2xl transition"
                >
                  {saving ? 'جاري الحفظ...' : '✅ إضافة المدير'}
                </button>

                <button
                  type="button"
                  onClick={resetForm}
                  disabled={saving}
                  className="bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-800 font-bold py-4 rounded-2xl transition"
                >
                  مسح الحقول
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-3xl shadow p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">📋 قائمة الموظفين</h2>
              <p className="text-gray-500 text-sm mt-1">عدد النتائج: {filteredEmployees.length}</p>
            </div>
          </div>

          {filteredEmployees.length === 0 ? (
            <div className="text-center text-gray-500 py-10 bg-gray-50 rounded-2xl">لا توجد نتائج مطابقة</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:hidden gap-4">
                {filteredEmployees.map((emp) => (
                  <EmployeeCard
                    key={emp.id}
                    emp={emp}
                    currentUser={currentUser}
                    getRoleIcon={getRoleIcon}
                    getRoleLabel={getRoleLabel}
                    getRoleClass={getRoleClass}
                    getActivityStatus={getActivityStatus}
                    getPageLabel={getPageLabel}
                    getDeviceLabel={getDeviceLabel}
                    getRelativeTime={getRelativeTime}
                    getLastSeenText={getLastSeenText}
                    formatDate={formatDate}
                    formatNumber={formatNumber}
                    onDelete={handleDelete}
                    deleting={deletingId === emp.id}
                    canSeeActivity={canSeeActivity || emp.id === currentUser?.id}
                  />
                ))}
              </div>

              <div className="hidden xl:block overflow-x-auto">
                <table className="w-full text-right border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-gray-700">
                      <th className="p-4 rounded-r-2xl">الموظف</th>
                      <th className="p-4">الصلاحية</th>
                      <th className="p-4">الحالة</th>
                      <th className="p-4">آخر ظهور</th>
                      <th className="p-4">آخر صفحة</th>
                      <th className="p-4">الجهاز</th>
                      <th className="p-4">الرصيد</th>
                      <th className="p-4">تاريخ الإنشاء</th>
                      <th className="p-4 rounded-l-2xl">إجراء</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredEmployees.map((emp) => {
                      const status = getActivityStatus(emp);
                      const isCurrentUser = emp.id === currentUser?.id;
                      const showActivity = canSeeActivity || isCurrentUser;

                      return (
                        <tr key={emp.id} className="border-b border-gray-100 hover:bg-orange-50/40">
                          <td className="p-4">
                            <div className="font-bold text-gray-900">{getRoleIcon(emp.role)} {emp.name}</div>
                            <div className="text-xs text-gray-400">{emp.id}</div>
                          </td>

                          <td className="p-4">
                            <span className={`inline-flex px-3 py-1 rounded-full text-sm font-bold border ${getRoleClass(emp.role)}`}>
                              {getRoleLabel(emp.role)}
                            </span>
                          </td>

                          <td className="p-4">
                            {showActivity ? (
                              <span className={`inline-flex px-3 py-1 rounded-full text-sm font-bold border ${status.className}`}>
                                {status.dot} {status.label}
                              </span>
                            ) : 'مخفي'}
                          </td>

                          <td className="p-4 text-gray-700">{showActivity ? getLastSeenText(emp) : 'مخفي'}</td>
                          <td className="p-4 text-gray-700">{showActivity ? getPageLabel(emp.last_page) : 'مخفي'}</td>
                          <td className="p-4 text-gray-700">{showActivity ? getDeviceLabel(emp.device_info) : 'مخفي'}</td>
                          <td className="p-4 font-bold text-gray-900">{formatNumber(emp.balance)}</td>
                          <td className="p-4 text-gray-600">{formatDate(emp.created_at)}</td>

                          <td className="p-4">
                            {emp.role === 'owner' ? (
                              <span className="text-yellow-700 font-bold">محمي</span>
                            ) : isCurrentUser ? (
                              <span className="text-blue-700 font-bold">حسابك</span>
                            ) : (
                              <button
                                onClick={() => handleDelete(emp)}
                                disabled={deletingId === emp.id}
                                className="bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-bold px-4 py-2 rounded-xl"
                              >
                                {deletingId === emp.id ? 'حذف...' : 'حذف'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function LoginHistorySection({ logs, loading, onRefresh, formatDate, canSee }) {
  const recentLogs = Array.isArray(logs) ? logs.slice(0, 15) : [];

  if (!canSee) {
    return null;
  }

  const getDeviceLabel = (log) => {
    const direct = log.device_label || '';
    if (direct) return direct;

    const agent = String(log.device_info || '').toLowerCase();
    if (agent.includes('android')) return 'هاتف Android';
    if (agent.includes('iphone') || agent.includes('ipad')) return 'هاتف iPhone / iPad';
    if (agent.includes('mobile')) return 'هاتف';
    if (agent.includes('windows')) return 'كمبيوتر Windows';
    if (agent.includes('mac')) return 'كمبيوتر Mac';
    return 'جهاز غير محدد';
  };

  const getRoleLabel = (role) => {
    if (role === 'owner') return 'مالك';
    if (role === 'manager') return 'مدير';
    return role || '-';
  };

  return (
    <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">🔐 سجل دخول المستخدمين</h2>
          <p className="text-gray-500 text-sm mt-1">
            يعرض آخر عمليات تسجيل الدخول التي تمت بعد تركيب تحديث Phase 1.
          </p>
        </div>

        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="bg-gray-800 hover:bg-black disabled:bg-gray-300 text-white font-bold py-3 px-5 rounded-xl"
        >
          {loading ? 'جاري التحديث...' : '🔄 تحديث السجل'}
        </button>
      </div>

      {recentLogs.length === 0 ? (
        <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 text-center text-gray-500">
          لا يوجد سجل دخول بعد. سجّل خروج ثم ادخل من جديد حتى يظهر أول سجل.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:hidden gap-4">
            {recentLogs.map((log) => (
              <div key={log.id} className="bg-gray-50 border border-gray-100 rounded-3xl p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h3 className="font-black text-gray-900 text-lg">{log.user_name || 'مستخدم'}</h3>
                    <p className="text-sm text-gray-500">{getRoleLabel(log.role)}</p>
                  </div>
                  <span className="bg-green-100 text-green-800 border border-green-200 rounded-full px-3 py-1 text-xs font-bold">
                    دخول
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <Info label="الوقت" value={formatDate(log.login_at)} />
                  <Info label="الجهاز" value={getDeviceLabel(log)} />
                  <div className="col-span-2">
                    <Info label="IP" value={log.ip_address || '-'} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden xl:block overflow-x-auto">
            <table className="w-full text-right border-collapse">
              <thead>
                <tr className="bg-gray-50 text-gray-700">
                  <th className="p-4 rounded-r-2xl">المستخدم</th>
                  <th className="p-4">الصلاحية</th>
                  <th className="p-4">الجهاز</th>
                  <th className="p-4">IP</th>
                  <th className="p-4 rounded-l-2xl">وقت الدخول</th>
                </tr>
              </thead>
              <tbody>
                {recentLogs.map((log) => (
                  <tr key={log.id} className="border-b border-gray-100 hover:bg-orange-50/40">
                    <td className="p-4 font-bold text-gray-900">{log.user_name || '-'}</td>
                    <td className="p-4 text-gray-700">{getRoleLabel(log.role)}</td>
                    <td className="p-4 text-gray-700">{getDeviceLabel(log)}</td>
                    <td className="p-4 text-gray-700">{log.ip_address || '-'}</td>
                    <td className="p-4 text-gray-700">{formatDate(log.login_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-white/10 rounded-2xl p-3 min-w-[88px]">
      <p className="text-2xl font-black">{value}</p>
      <p className="text-xs text-orange-100 mt-1">{label}</p>
    </div>
  );
}

function RuleCard({ title, text, color }) {
  const classes = {
    yellow: 'bg-yellow-50 border-yellow-100 text-yellow-800',
    purple: 'bg-purple-50 border-purple-100 text-purple-800',
    green: 'bg-green-50 border-green-100 text-green-800'
  };

  return (
    <div className={`border rounded-2xl p-4 ${classes[color] || classes.green}`}>
      <h3 className="font-bold text-lg mb-2">{title}</h3>
      <p className="text-sm leading-6 text-gray-700">{text}</p>
    </div>
  );
}

function ActiveUserCard({ employee, getActivityStatus, getRoleLabel, getPageLabel, getDeviceLabel, getRelativeTime, getLastSeenText }) {
  const status = getActivityStatus(employee);

  return (
    <div className="border border-gray-100 rounded-3xl p-4 bg-gradient-to-br from-white to-green-50 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-xl font-black text-gray-900">{employee.name}</h3>
          <p className="text-sm text-gray-500">{getRoleLabel(employee.role)}</p>
        </div>

        <span className={`px-3 py-1 rounded-full text-sm font-bold border ${status.className}`}>
          {status.dot} {status.label}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-sm">
        <Info label="آخر ظهور" value={getLastSeenText ? getLastSeenText(employee) : getRelativeTime(employee.last_seen_at)} />
        <Info label="آخر صفحة" value={getPageLabel(employee.last_page)} />
        <div className="col-span-2">
          <Info label="الجهاز" value={getDeviceLabel(employee.device_info)} />
        </div>
      </div>
    </div>
  );
}

function EmployeeCard({
  emp,
  currentUser,
  getRoleIcon,
  getRoleLabel,
  getRoleClass,
  getActivityStatus,
  getPageLabel,
  getDeviceLabel,
  getRelativeTime,
  getLastSeenText,
  formatDate,
  formatNumber,
  onDelete,
  deleting,
  canSeeActivity
}) {
  const isCurrentUser = emp.id === currentUser?.id;
  const status = getActivityStatus(emp);

  return (
    <div className="bg-gray-50 border border-gray-100 rounded-3xl p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xl font-black text-gray-900">{getRoleIcon(emp.role)} {emp.name}</h3>
          <p className="text-xs text-gray-400 mt-1 break-all">{emp.id}</p>
        </div>

        <span className={`px-3 py-1 rounded-full text-sm font-bold border ${getRoleClass(emp.role)}`}>
          {getRoleLabel(emp.role)}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <Info label="الرصيد" value={formatNumber(emp.balance)} />
        <Info label="تاريخ الإنشاء" value={formatDate(emp.created_at)} />
        <Info label="الحالة" value={canSeeActivity ? `${status.dot} ${status.label}` : 'مخفي'} />
        <Info label="آخر ظهور" value={canSeeActivity ? (getLastSeenText ? getLastSeenText(emp) : getRelativeTime(emp.last_seen_at)) : 'مخفي'} />
        <Info label="آخر صفحة" value={canSeeActivity ? getPageLabel(emp.last_page) : 'مخفي'} />
        <Info label="الجهاز" value={canSeeActivity ? getDeviceLabel(emp.device_info) : 'مخفي'} />
      </div>

      {emp.role === 'owner' ? (
        <div className="bg-yellow-50 border border-yellow-100 text-yellow-700 rounded-2xl p-3 text-sm font-bold">
           المالك محمي ولا يمكن حذفه
        </div>
      ) : isCurrentUser ? (
        <div className="bg-blue-50 border border-blue-100 text-blue-700 rounded-2xl p-3 text-sm font-bold">
          هذا حسابك الحالي
        </div>
      ) : (
        <button
          onClick={() => onDelete(emp)}
          disabled={deleting}
          className="w-full bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-bold py-3 rounded-2xl"
        >
          {deleting ? 'جاري الحذف...' : 'حذف الموظف'}
        </button>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-white rounded-2xl p-3 border border-gray-100">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="font-bold text-gray-900 truncate">{value}</p>
    </div>
  );
}

export default Employees;
